// Salvar e imprimir arquivos do painel, no navegador e no app nativo.
//
// No navegador: download por <a download> e impressão numa janela nova.
// No app (WebView) nenhum dos dois funciona: o arquivo vai para o cache do
// aparelho e abre a folha de compartilhar do sistema (salvar em Arquivos,
// mandar no WhatsApp...), e a impressão usa o plugin nativo "Imprimir"
// (ImprimirPlugin no iOS e no Android), que também salva como PDF.

import { registerPlugin } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { ehNativo } from './nativo';

interface ImprimirPlugin {
  html(opcoes: { html: string; titulo: string }): Promise<void>;
}

const Imprimir = registerPlugin<ImprimirPlugin>('Imprimir');

function paraBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => resolve(String(leitor.result).split(',')[1] ?? '');
    leitor.onerror = () => reject(leitor.error);
    leitor.readAsDataURL(blob);
  });
}

// Nome seguro para o sistema de arquivos (o nome original pode ter "/").
function nomeSeguro(nome: string) {
  return nome.replace(/[\\/:*?"<>|]+/g, '_').trim() || 'arquivo';
}

export async function salvarArquivo(blob: Blob, nome: string): Promise<void> {
  if (!ehNativo) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nome;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
    return;
  }

  const { uri } = await Filesystem.writeFile({
    path: nomeSeguro(nome),
    data: await paraBase64(blob),
    directory: Directory.Cache,
  });
  try {
    await Share.share({ title: nome, files: [uri] });
  } catch (error) {
    // Fechar a folha sem escolher nada não é erro.
    if (!String(error).toLowerCase().includes('cancel')) throw error;
  }
}

// Documento HTML pronto para imprimir (proposta, comprovante, relatório). O
// HTML pode trazer onload="window.print()" para o navegador; no app ele é
// removido e a impressão é chamada pelo plugin nativo.
export async function imprimirHtml(
  html: string,
  titulo: string,
  janela = 'width=840,height=1040'
): Promise<void> {
  if (ehNativo) {
    await Imprimir.html({ html: html.replace(/\sonload="window\.print\(\)"/, ''), titulo });
    return;
  }
  const w = window.open('', '_blank', janela);
  if (!w) return;
  w.document.write(html);
  w.document.close();
  w.focus();
}
